/**
 * Visual Effects Types
 *
 * Types for the Effects overlay system that displays visual effects
 * on the player's screen triggered by game events.
 *
 * Architecture:
 * - Game events trigger effects via event-to-effect mappings
 * - Admins configure which effects play for which events
 * - Each game type has its own set of available events
 */
import { GameType } from '../constants/gameTypes';
// ============================================================================
// Effect Type Definitions
// ============================================================================
/**
 * Available effect types
 * This enum defines all visual effects that can be triggered
 */
export var EffectType;
(function (EffectType) {
    /** Glowing border around the screen edges */
    EffectType["BORDER_GLOW"] = "border_glow";
    /** @deprecated Use TS_CONFETTI or a specific tsParticles confetti variant instead */
    EffectType["CONFETTI_DEPRECATED"] = "confetti";
    // tsParticles preset effects
    EffectType["TS_AMBIENT"] = "ts_ambient";
    EffectType["TS_BIG_CIRCLES"] = "ts_big_circles";
    EffectType["TS_BUBBLES"] = "ts_bubbles";
    EffectType["TS_CONFETTI"] = "ts_confetti";
    EffectType["TS_CONFETTI_CANNON"] = "ts_confetti_cannon";
    EffectType["TS_CONFETTI_EXPLOSIONS"] = "ts_confetti_explosions";
    EffectType["TS_CONFETTI_FALLING"] = "ts_confetti_falling";
    EffectType["TS_CONFETTI_PARADE"] = "ts_confetti_parade";
    EffectType["TS_FIRE"] = "ts_fire";
    EffectType["TS_FIREFLY"] = "ts_firefly";
    EffectType["TS_FIREWORKS"] = "ts_fireworks";
    EffectType["TS_FOUNTAIN"] = "ts_fountain";
    EffectType["TS_HYPERSPACE"] = "ts_hyperspace";
    EffectType["TS_LINKS"] = "ts_links";
    EffectType["TS_MATRIX"] = "ts_matrix";
    EffectType["TS_METEORS"] = "ts_meteors";
    EffectType["TS_PARTY"] = "ts_party";
    EffectType["TS_SEA_ANEMONE"] = "ts_sea_anemone";
    EffectType["TS_SNOW"] = "ts_snow";
    EffectType["TS_SQUARES"] = "ts_squares";
    EffectType["TS_STARS"] = "ts_stars";
    EffectType["TS_TRIANGLES"] = "ts_triangles";
})(EffectType || (EffectType = {}));
/**
 * Get display name for an effect type
 */
export function getEffectTypeDisplayName(effectType) {
    switch (effectType) {
        case EffectType.BORDER_GLOW: return 'Border Glow';
        case EffectType.CONFETTI_DEPRECATED: return 'Confetti (Deprecated)';
        case EffectType.TS_AMBIENT: return 'Ambient';
        case EffectType.TS_BIG_CIRCLES: return 'Big Circles';
        case EffectType.TS_BUBBLES: return 'Bubbles';
        case EffectType.TS_CONFETTI: return 'Confetti';
        case EffectType.TS_CONFETTI_CANNON: return 'Confetti Cannon';
        case EffectType.TS_CONFETTI_EXPLOSIONS: return 'Confetti Explosions';
        case EffectType.TS_CONFETTI_FALLING: return 'Confetti Falling';
        case EffectType.TS_CONFETTI_PARADE: return 'Confetti Parade';
        case EffectType.TS_FIRE: return 'Fire';
        case EffectType.TS_FIREFLY: return 'Firefly';
        case EffectType.TS_FIREWORKS: return 'Fireworks';
        case EffectType.TS_FOUNTAIN: return 'Fountain';
        case EffectType.TS_HYPERSPACE: return 'Hyperspace';
        case EffectType.TS_LINKS: return 'Links';
        case EffectType.TS_MATRIX: return 'Matrix';
        case EffectType.TS_METEORS: return 'Meteors';
        case EffectType.TS_PARTY: return 'Party';
        case EffectType.TS_SEA_ANEMONE: return 'Sea Anemone';
        case EffectType.TS_SNOW: return 'Snow';
        case EffectType.TS_SQUARES: return 'Squares';
        case EffectType.TS_STARS: return 'Stars';
        case EffectType.TS_TRIANGLES: return 'Triangles';
        default: return 'Unknown Effect';
    }
}
/**
 * Get all available effect types
 */
export function getAllEffectTypes() {
    return Object.values(EffectType);
}
// ============================================================================
// Game-Specific Effect Events
// ============================================================================
/**
 * Events that can trigger effects in Hide and Seek games
 */
export var HnsEffectEvent;
(function (HnsEffectEvent) {
    /** Player was disqualified for cheating */
    HnsEffectEvent["PLAYER_DISQUALIFIED"] = "hns_player_disqualified";
    /** Fires only for the player who was caught (self view) */
    HnsEffectEvent["PLAYER_CAUGHT"] = "hns_player_caught";
    /** Fires for every other player when someone else is caught */
    HnsEffectEvent["OTHER_CAUGHT"] = "hns_other_caught";
    /** Round has started */
    HnsEffectEvent["ROUND_START"] = "hns_round_start";
    /** Round has ended */
    HnsEffectEvent["ROUND_END"] = "hns_round_end";
    /** Game has finished */
    HnsEffectEvent["GAME_FINISHED"] = "hns_game_finished";
})(HnsEffectEvent || (HnsEffectEvent = {}));
/**
 * Events that can trigger effects in Flashpoint Race games
 */
export var FprEffectEvent;
(function (FprEffectEvent) {
    /** Player died */
    FprEffectEvent["PLAYER_DEATH"] = "fpr_player_death";
    /** Team killed a non-final boss in the flashpoint */
    FprEffectEvent["BOSS_KILLED"] = "fpr_boss_killed";
    /** Team killed the final boss of the flashpoint */
    FprEffectEvent["FINAL_BOSS_KILLED"] = "fpr_final_boss_killed";
    /** Team finished the race */
    FprEffectEvent["TEAM_FINISHED"] = "fpr_team_finished";
    /** Team entered their flashpoint (per-team, fires when AreaEntered detected) */
    FprEffectEvent["FP_ENTERED"] = "fpr_fp_entered";
    /** Round has started (admin triggered, global) */
    FprEffectEvent["ROUND_START"] = "fpr_round_start";
    /** Round has ended */
    FprEffectEvent["ROUND_END"] = "fpr_round_end";
    /** Game has finished */
    FprEffectEvent["GAME_FINISHED"] = "fpr_game_finished";
})(FprEffectEvent || (FprEffectEvent = {}));
/**
 * Events that can trigger effects in Hide and Seek Teams games
 */
export var HnstEffectEvent;
(function (HnstEffectEvent) {
    /** Player was disqualified for cheating */
    HnstEffectEvent["PLAYER_DISQUALIFIED"] = "hnst_player_disqualified";
    /** Fires only for the hider who was caught (self view) */
    HnstEffectEvent["YOU_CAUGHT"] = "hnst_you_caught";
    /** Fires for the caught hider's teammates */
    HnstEffectEvent["TEAMMATE_CAUGHT"] = "hnst_teammate_caught";
    /** Fires for the seeking team when one of them catches a hider */
    HnstEffectEvent["OPPONENT_CAUGHT"] = "hnst_opponent_caught";
    /** Fires only for a hider eliminated by dying more often than the Deaths rule allows (self view) */
    HnstEffectEvent["YOU_ELIMINATED"] = "hnst_you_eliminated";
    /** Every hider is out: the round is over */
    HnstEffectEvent["TEAM_WIPED"] = "hnst_team_wiped";
    /** The coin landed (who chooses to hide or seek first) */
    HnstEffectEvent["COIN_FLIP"] = "hnst_coin_flip";
    /** Round has started */
    HnstEffectEvent["ROUND_START"] = "hnst_round_start";
    /** The hiding time is up: the seekers are released */
    HnstEffectEvent["HIDING_COMPLETE"] = "hnst_hiding_complete";
    /** Round has ended */
    HnstEffectEvent["ROUND_END"] = "hnst_round_end";
    /** Game has finished */
    HnstEffectEvent["GAME_FINISHED"] = "hnst_game_finished";
})(HnstEffectEvent || (HnstEffectEvent = {}));
/**
 * Events that can trigger effects in Trailblazers games
 */
export var TrlEffectEvent;
(function (TrlEffectEvent) {
    /** Round has started (a new screenshot is revealed) */
    TrlEffectEvent["ROUND_START"] = "trl_round_start";
    /** A team captured the treasure (within the winning radar range) */
    TrlEffectEvent["TREASURE_FOUND"] = "trl_treasure_found";
    /** The digging player missed (dug beyond the last radar range) */
    TrlEffectEvent["DIG_MISS"] = "trl_miss";
    /** Round has ended */
    TrlEffectEvent["ROUND_END"] = "trl_round_end";
    /** Game has finished */
    TrlEffectEvent["GAME_FINISHED"] = "trl_game_finished";
})(TrlEffectEvent || (TrlEffectEvent = {}));
/**
 * Get all available events for a specific game type
 */
export function getEventsForGameType(gameType) {
    switch (gameType) {
        case GameType.HIDE_AND_SEEK:
            return Object.values(HnsEffectEvent);
        case GameType.FP_RACE:
            return Object.values(FprEffectEvent);
        case GameType.TRAILBLAZERS:
            return Object.values(TrlEffectEvent);
        case GameType.HIDE_AND_SEEK_TEAMS:
            return Object.values(HnstEffectEvent);
        default:
            return [];
    }
}
/**
 * Get display name for an effect event
 */
export function getEventDisplayName(event) {
    const names = {
        // HNS events
        [HnsEffectEvent.PLAYER_DISQUALIFIED]: 'Player Disqualified',
        [HnsEffectEvent.PLAYER_CAUGHT]: 'Player Caught (Self)',
        [HnsEffectEvent.OTHER_CAUGHT]: 'Player Caught (Others)',
        [HnsEffectEvent.ROUND_START]: 'Round Start',
        [HnsEffectEvent.ROUND_END]: 'Round End',
        [HnsEffectEvent.GAME_FINISHED]: 'Game Finished',
        // FPR events
        [FprEffectEvent.PLAYER_DEATH]: 'Player Death',
        [FprEffectEvent.BOSS_KILLED]: 'Boss Killed',
        [FprEffectEvent.FINAL_BOSS_KILLED]: 'Final Boss Killed',
        [FprEffectEvent.TEAM_FINISHED]: 'Team Finished',
        [FprEffectEvent.FP_ENTERED]: 'Flashpoint Entered',
        [FprEffectEvent.ROUND_START]: 'Round Start',
        [FprEffectEvent.ROUND_END]: 'Round End',
        [FprEffectEvent.GAME_FINISHED]: 'Game Finished',
        // TRL events
        [TrlEffectEvent.ROUND_START]: 'Round Start',
        [TrlEffectEvent.TREASURE_FOUND]: 'Treasure Found',
        [TrlEffectEvent.DIG_MISS]: 'Dig Miss',
        [TrlEffectEvent.ROUND_END]: 'Round End',
        [TrlEffectEvent.GAME_FINISHED]: 'Game Finished',
        // HNST events
        [HnstEffectEvent.PLAYER_DISQUALIFIED]: 'Player Disqualified',
        [HnstEffectEvent.YOU_CAUGHT]: 'Caught (Self)',
        [HnstEffectEvent.TEAMMATE_CAUGHT]: 'Teammate Caught',
        [HnstEffectEvent.OPPONENT_CAUGHT]: 'Opponent Caught',
        [HnstEffectEvent.YOU_ELIMINATED]: 'Eliminated by Deaths (Self)',
        [HnstEffectEvent.TEAM_WIPED]: 'All Hiders Caught',
        [HnstEffectEvent.COIN_FLIP]: 'Coin Flip',
        [HnstEffectEvent.ROUND_START]: 'Round Start',
        [HnstEffectEvent.HIDING_COMPLETE]: 'Hiding Over (Seekers Released)',
        [HnstEffectEvent.ROUND_END]: 'Round End',
        [HnstEffectEvent.GAME_FINISHED]: 'Game Finished',
    };
    return names[event] || 'Unknown Event';
}
/**
 * Get emoji icon for an effect event
 */
export function getEventIcon(event) {
    const icons = {
        // HNS events
        [HnsEffectEvent.PLAYER_DISQUALIFIED]: '🔴',
        [HnsEffectEvent.PLAYER_CAUGHT]: '🏃',
        [HnsEffectEvent.OTHER_CAUGHT]: '👀',
        [HnsEffectEvent.ROUND_START]: '▶️',
        [HnsEffectEvent.ROUND_END]: '⏹️',
        [HnsEffectEvent.GAME_FINISHED]: '🎉',
        // FPR events
        [FprEffectEvent.PLAYER_DEATH]: '💀',
        [FprEffectEvent.BOSS_KILLED]: '🗡️',
        [FprEffectEvent.FINAL_BOSS_KILLED]: '👑',
        [FprEffectEvent.TEAM_FINISHED]: '🏁',
        [FprEffectEvent.FP_ENTERED]: '🚪',
        [FprEffectEvent.ROUND_START]: '▶️',
        [FprEffectEvent.ROUND_END]: '⏹️',
        [FprEffectEvent.GAME_FINISHED]: '🎉',
        // TRL events
        [TrlEffectEvent.ROUND_START]: '🧭',
        [TrlEffectEvent.TREASURE_FOUND]: '💰',
        [TrlEffectEvent.DIG_MISS]: '🕳️',
        [TrlEffectEvent.ROUND_END]: '⏹️',
        [TrlEffectEvent.GAME_FINISHED]: '🎉',
        // HNST events
        [HnstEffectEvent.PLAYER_DISQUALIFIED]: '🔴',
        [HnstEffectEvent.YOU_CAUGHT]: '🏃',
        [HnstEffectEvent.TEAMMATE_CAUGHT]: '😬',
        [HnstEffectEvent.OPPONENT_CAUGHT]: '🎯',
        [HnstEffectEvent.YOU_ELIMINATED]: '💀',
        [HnstEffectEvent.TEAM_WIPED]: '🧹',
        [HnstEffectEvent.COIN_FLIP]: '🪙',
        [HnstEffectEvent.ROUND_START]: '▶️',
        [HnstEffectEvent.HIDING_COMPLETE]: '🔓',
        [HnstEffectEvent.ROUND_END]: '⏹️',
        [HnstEffectEvent.GAME_FINISHED]: '🎉',
    };
    return icons[event] || '❓';
}
/**
 * Default confetti colors - festive mix
 */
export const DEFAULT_CONFETTI_COLORS = [
    '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
    '#ffa500', '#ff69b4', '#ffd700', '#c0c0c0',
];
/**
 * Default configurations for each effect type
 */
export const DEFAULT_EFFECT_CONFIGS = {
    [EffectType.BORDER_GLOW]: {
        type: EffectType.BORDER_GLOW,
        color: '#ff0000',
        durationMs: 3000,
        intensity: 0.8,
        borderWidth: 20,
        animated: true,
    },
    [EffectType.CONFETTI_DEPRECATED]: {
        type: EffectType.CONFETTI_DEPRECATED,
        durationMs: 5000,
        preset: 'fireworks',
        particleCount: 50,
        angle: 90,
        spread: 360,
        startVelocity: 45,
        decay: 0.9,
        gravity: 1,
        drift: 0,
        ticks: 200,
        colors: DEFAULT_CONFETTI_COLORS,
        shapes: ['square', 'circle'],
        scalar: 1,
        flat: false,
        origin: { x: 0.5, y: 0.5 },
    },
    // tsParticles presets — burst/celebration effects (6s)
    [EffectType.TS_CONFETTI]: { type: EffectType.TS_CONFETTI, durationMs: 6000 },
    [EffectType.TS_CONFETTI_CANNON]: { type: EffectType.TS_CONFETTI_CANNON, durationMs: 6000 },
    [EffectType.TS_CONFETTI_EXPLOSIONS]: { type: EffectType.TS_CONFETTI_EXPLOSIONS, durationMs: 6000 },
    [EffectType.TS_CONFETTI_FALLING]: { type: EffectType.TS_CONFETTI_FALLING, durationMs: 6000 },
    [EffectType.TS_CONFETTI_PARADE]: { type: EffectType.TS_CONFETTI_PARADE, durationMs: 6000 },
    [EffectType.TS_FIRE]: { type: EffectType.TS_FIRE, durationMs: 6000 },
    [EffectType.TS_FIREWORKS]: { type: EffectType.TS_FIREWORKS, durationMs: 6000 },
    [EffectType.TS_FOUNTAIN]: { type: EffectType.TS_FOUNTAIN, durationMs: 6000 },
    [EffectType.TS_METEORS]: { type: EffectType.TS_METEORS, durationMs: 6000 },
    [EffectType.TS_PARTY]: { type: EffectType.TS_PARTY, durationMs: 6000 },
    // tsParticles presets — ambient/looping effects (8s)
    [EffectType.TS_AMBIENT]: { type: EffectType.TS_AMBIENT, durationMs: 8000 },
    [EffectType.TS_BIG_CIRCLES]: { type: EffectType.TS_BIG_CIRCLES, durationMs: 8000 },
    [EffectType.TS_BUBBLES]: { type: EffectType.TS_BUBBLES, durationMs: 8000 },
    [EffectType.TS_FIREFLY]: { type: EffectType.TS_FIREFLY, durationMs: 8000 },
    [EffectType.TS_HYPERSPACE]: { type: EffectType.TS_HYPERSPACE, durationMs: 8000 },
    [EffectType.TS_LINKS]: { type: EffectType.TS_LINKS, durationMs: 8000 },
    [EffectType.TS_MATRIX]: { type: EffectType.TS_MATRIX, durationMs: 8000 },
    [EffectType.TS_SEA_ANEMONE]: { type: EffectType.TS_SEA_ANEMONE, durationMs: 8000 },
    [EffectType.TS_SNOW]: { type: EffectType.TS_SNOW, durationMs: 8000 },
    [EffectType.TS_SQUARES]: { type: EffectType.TS_SQUARES, durationMs: 8000 },
    [EffectType.TS_STARS]: { type: EffectType.TS_STARS, durationMs: 8000 },
    [EffectType.TS_TRIANGLES]: { type: EffectType.TS_TRIANGLES, durationMs: 8000 },
};
/**
 * Preset configurations for confetti - partial overrides for the default config
 */
export const CONFETTI_PRESETS = {
    fireworks: {
        // Based on canvas-confetti fireworks example
        // Launches bursts from random positions every 250ms
        particleCount: 50,
        spread: 360,
        startVelocity: 30,
        decay: 0.9,
        gravity: 1,
        ticks: 60,
        origin: { x: 0.5, y: 0.3 },
    },
    realistic: {
        particleCount: 100,
        spread: 60,
        startVelocity: 45,
        decay: 0.9,
        gravity: 1,
        origin: { x: 0.5, y: 0.3 },
    },
    explosion: {
        particleCount: 200,
        spread: 360,
        startVelocity: 60,
        decay: 0.85,
        gravity: 0.8,
        origin: { x: 0.5, y: 0.5 },
    },
    pride: {
        particleCount: 150,
        spread: 180,
        startVelocity: 55,
        colors: ['#e40303', '#ff8c00', '#ffed00', '#008026', '#24408e', '#732982'],
        origin: { x: 0.5, y: 0.5 },
    },
    vortex: {
        particleCount: 75,
        spread: 45,
        startVelocity: 50,
        drift: 0.5,
        gravity: 0.5,
        ticks: 300,
        origin: { x: 0.5, y: 0.5 },
    },
    custom: {
    // No overrides - use whatever values are set
    },
};
/**
 * Apply a preset to a confetti config
 */
export function applyConfettiPreset(config, preset) {
    const presetValues = CONFETTI_PRESETS[preset];
    return {
        ...config,
        ...presetValues,
        preset,
    };
}
/**
 * Generate a unique ID for a new effect mapping
 */
export function generateEffectMappingId() {
    return `effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
/**
 * Trailblazers scan-result edge-glow colours, shared so the border-glow effect
 * and the scan-status overlay/banners stay in lock-step (no drift):
 *   - found  → green  (planet cleared / treasure found)
 *   - close  → orange (near the target)
 *   - miss   → black  (nothing here)
 */
export const TRL_GLOW_COLORS = {
    found: '#2ecc71',
    close: '#f4a259',
    miss: '#000000',
};
/**
 * Get default effect mappings for a game type
 * These are the mappings created when a new game is started
 */
export function getDefaultEffectMappings(gameType) {
    switch (gameType) {
        case GameType.HIDE_AND_SEEK:
            return [
                {
                    id: 'default-hns-disqualified',
                    event: HnsEffectEvent.PLAYER_DISQUALIFIED,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#ff0000',
                        durationMs: 3000,
                        intensity: 0.8,
                        borderWidth: 20,
                        animated: true,
                    },
                    enabled: true,
                },
                {
                    id: 'default-hns-game-finished',
                    event: HnsEffectEvent.GAME_FINISHED,
                    effectType: EffectType.CONFETTI_DEPRECATED,
                    config: {
                        type: EffectType.CONFETTI_DEPRECATED,
                        durationMs: 7500,
                        preset: 'fireworks',
                        particleCount: 50,
                        angle: 90,
                        spread: 360,
                        startVelocity: 30,
                        decay: 0.9,
                        gravity: 1,
                        drift: 0,
                        ticks: 60,
                        colors: DEFAULT_CONFETTI_COLORS,
                        shapes: ['square', 'circle'],
                        scalar: 1,
                        flat: false,
                        origin: { x: 0.5, y: 0.3 },
                    },
                    enabled: true,
                },
                {
                    id: 'default-hns-caught',
                    event: HnsEffectEvent.PLAYER_CAUGHT,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#1de8f2',
                        durationMs: 2000,
                        intensity: 0.8,
                        borderWidth: 20,
                        animated: true,
                    },
                    enabled: true,
                },
            ];
        case GameType.FP_RACE:
            return [
                {
                    id: 'default-fpr-round-start',
                    event: FprEffectEvent.ROUND_START,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#000000',
                        durationMs: 7500,
                        intensity: 1.0,
                        borderWidth: 30,
                        animated: false,
                    },
                    enabled: true,
                },
                {
                    id: 'default-fpr-fp-entered',
                    event: FprEffectEvent.FP_ENTERED,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#4ECDC4',
                        durationMs: 2000,
                        intensity: 0.7,
                        borderWidth: 15,
                        animated: true,
                    },
                    enabled: true,
                },
                {
                    id: 'default-fpr-team-finished',
                    event: FprEffectEvent.TEAM_FINISHED,
                    effectType: EffectType.CONFETTI_DEPRECATED,
                    config: {
                        type: EffectType.CONFETTI_DEPRECATED,
                        durationMs: 7500,
                        preset: 'fireworks',
                        particleCount: 50,
                        angle: 90,
                        spread: 360,
                        startVelocity: 30,
                        decay: 0.9,
                        gravity: 1,
                        drift: 0,
                        ticks: 60,
                        colors: DEFAULT_CONFETTI_COLORS,
                        shapes: ['square', 'circle'],
                        scalar: 1,
                        flat: false,
                        origin: { x: 0.5, y: 0.3 },
                    },
                    enabled: true,
                },
                {
                    id: 'default-fpr-game-finished',
                    event: FprEffectEvent.GAME_FINISHED,
                    effectType: EffectType.CONFETTI_DEPRECATED,
                    config: {
                        type: EffectType.CONFETTI_DEPRECATED,
                        durationMs: 7500,
                        preset: 'fireworks',
                        particleCount: 50,
                        angle: 90,
                        spread: 360,
                        startVelocity: 30,
                        decay: 0.9,
                        gravity: 1,
                        drift: 0,
                        ticks: 60,
                        colors: DEFAULT_CONFETTI_COLORS,
                        shapes: ['square', 'circle'],
                        scalar: 1,
                        flat: false,
                        origin: { x: 0.5, y: 0.3 },
                    },
                    enabled: true,
                },
            ];
        case GameType.TRAILBLAZERS:
            return [
                {
                    id: 'default-trl-treasure-found',
                    event: TrlEffectEvent.TREASURE_FOUND,
                    effectType: EffectType.BORDER_GLOW,
                    // "Found" preset: a pulsing green edge glow celebrating a cleared planet.
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: TRL_GLOW_COLORS.found,
                        durationMs: 2500,
                        intensity: 0.75,
                        borderWidth: 15,
                        animated: true,
                        depth: 0.3,
                    },
                    enabled: true,
                },
                {
                    id: 'default-trl-dig-miss',
                    event: TrlEffectEvent.DIG_MISS,
                    effectType: EffectType.BORDER_GLOW,
                    // "Nothing" preset: a small, static black edge fade on a miss.
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: TRL_GLOW_COLORS.miss,
                        durationMs: 1200,
                        intensity: 0.6,
                        borderWidth: 15,
                        animated: false,
                        depth: 0.12,
                    },
                    enabled: true,
                },
                {
                    id: 'default-trl-game-finished',
                    event: TrlEffectEvent.GAME_FINISHED,
                    effectType: EffectType.CONFETTI_DEPRECATED,
                    config: {
                        type: EffectType.CONFETTI_DEPRECATED,
                        durationMs: 7500,
                        preset: 'fireworks',
                        particleCount: 50,
                        angle: 90,
                        spread: 360,
                        startVelocity: 30,
                        decay: 0.9,
                        gravity: 1,
                        drift: 0,
                        ticks: 60,
                        colors: DEFAULT_CONFETTI_COLORS,
                        shapes: ['square', 'circle'],
                        scalar: 1,
                        flat: false,
                        origin: { x: 0.5, y: 0.3 },
                    },
                    enabled: true,
                },
            ];
        case GameType.HIDE_AND_SEEK_TEAMS:
            return [
                {
                    id: 'default-hnst-disqualified',
                    event: HnstEffectEvent.PLAYER_DISQUALIFIED,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#ff0000',
                        durationMs: 3000,
                        intensity: 0.8,
                        borderWidth: 20,
                        animated: true,
                    },
                    enabled: true,
                },
                {
                    id: 'default-hnst-you-caught',
                    event: HnstEffectEvent.YOU_CAUGHT,
                    effectType: EffectType.BORDER_GLOW,
                    config: {
                        type: EffectType.BORDER_GLOW,
                        color: '#1de8f2',
                        durationMs: 2000,
                        intensity: 0.8,
                        borderWidth: 20,
                        animated: true,
                    },
                    enabled: true,
                },
                {
                    id: 'default-hnst-game-finished',
                    event: HnstEffectEvent.GAME_FINISHED,
                    effectType: EffectType.CONFETTI_DEPRECATED,
                    config: {
                        type: EffectType.CONFETTI_DEPRECATED,
                        durationMs: 7500,
                        preset: 'fireworks',
                        particleCount: 50,
                        angle: 90,
                        spread: 360,
                        startVelocity: 30,
                        decay: 0.9,
                        gravity: 1,
                        drift: 0,
                        ticks: 60,
                        colors: DEFAULT_CONFETTI_COLORS,
                        shapes: ['square', 'circle'],
                        scalar: 1,
                        flat: false,
                        origin: { x: 0.5, y: 0.3 },
                    },
                    enabled: true,
                },
            ];
        default:
            return [];
    }
}
/**
 * Get default effect settings for a game type
 */
export function getDefaultEffectSettings(gameType) {
    return {
        mappings: getDefaultEffectMappings(gameType),
    };
}
/**
 * Create a new effect mapping with default config for the effect type
 */
export function createEffectMapping(event, effectType) {
    return {
        id: generateEffectMappingId(),
        event,
        effectType,
        config: { ...DEFAULT_EFFECT_CONFIGS[effectType] },
        enabled: true,
    };
}
/**
 * @deprecated Use getDefaultEffectSettings instead
 * Default effect settings (old format)
 */
export const DEFAULT_EFFECT_SETTINGS = {
    configs: {
        ...DEFAULT_EFFECT_CONFIGS,
    },
};
// ============================================================================
// Type Guards
// ============================================================================
/**
 * Type guard for BorderGlowEffectConfig
 */
export function isBorderGlowEffectConfig(config) {
    return config.type === EffectType.BORDER_GLOW;
}
/**
 * Type guard for ConfettiEffectConfig
 */
export function isConfettiEffectConfig(config) {
    return config.type === EffectType.CONFETTI_DEPRECATED;
}
/**
 * Type guard for TsParticlesEffectConfig
 */
export function isTsParticlesEffectConfig(config) {
    return typeof config.type === 'string' && config.type.startsWith('ts_');
}
/**
 * Returns true for any EffectType that maps to a tsParticles preset
 */
export function isTsParticlesEffectType(type) {
    return type.startsWith('ts_');
}
/**
 * Type guard for EffectTriggerPayload
 */
export function isEffectTriggerPayload(data) {
    return (typeof data === 'object' &&
        data !== null &&
        'effectType' in data &&
        'event' in data &&
        Object.values(EffectType).includes(data.effectType));
}
/**
 * Type guard for HnsEffectEvent
 */
export function isHnsEffectEvent(event) {
    return Object.values(HnsEffectEvent).includes(event);
}
/**
 * Type guard for FprEffectEvent
 */
export function isFprEffectEvent(event) {
    return Object.values(FprEffectEvent).includes(event);
}
/**
 * Type guard for TrlEffectEvent
 */
export function isTrlEffectEvent(event) {
    return Object.values(TrlEffectEvent).includes(event);
}
/**
 * Type guard for HnstEffectEvent
 */
export function isHnstEffectEvent(event) {
    return Object.values(HnstEffectEvent).includes(event);
}
